import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme, useMediaQuery, Paper, BottomNavigation, BottomNavigationAction, Dialog, DialogTitle, DialogContent, Button, Stack, Typography } from '@mui/material';
import { Home, Article, Person, Settings, Restaurant, MeetingRoom } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import useAuth from '../../../hooks/useAuth';
import useAccessProvider, { hasMemberDoorsUI } from '../../../hooks/useAccessProvider';

const useScrollDirection = () => {
  const [scrollDirection, setScrollDirection] = useState('up');
  const [prevOffset, setPrevOffset] = useState(0);
  const threshold = 5;

  useEffect(() => {
    const handleScroll = () => {
      const currentOffset = window.pageYOffset;
      const isAtTop = currentOffset === 0;
      const isAtBottom = window.innerHeight + currentOffset >= document.documentElement.scrollHeight;

      if (isAtTop) {
        setScrollDirection('up');
      } else if (isAtBottom) {
        setScrollDirection('down');
      } else {
        const diff = currentOffset - prevOffset;
        const direction = diff > threshold ? 'down' : diff < -threshold ? 'up' : scrollDirection;
        setScrollDirection(direction);
      }

      setPrevOffset(currentOffset);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollDirection, prevOffset]);

  return scrollDirection;
};

const MobileBottomNav = ({ header }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState('home');
  const scrollDirection = useScrollDirection();
  const { user } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [noPlanDialogOpen, setNoPlanDialogOpen] = useState(false);

  // Feature flags from orgPlan
  const hasCafeFeature = user?.orgPlan?.features?.some((f) => f.slug === 'cafe') ?? false;
  // The feed route is guarded by slug="community"; without the same check here
  // the tab is a one-way trip to a 403 page for orgs without the module.
  // Guests belong to no team, and the server refuses them the feed.
  const hasCommunityFeature =
    user?.account_type !== 'guest' && (user?.orgPlan?.features?.some((f) => f.slug === 'community') ?? false);
  const { provider: accessProvider, capabilities: accessCapabilities } = useAccessProvider();

  // A door tab is only meaningful when a provider is live AND that provider can
  // actually do something for a member. Checking a capability rather than the
  // slug means an unrecognised provider (one with no adapter registered, so the
  // backend reports empty capabilities) fails closed instead of routing members
  // to a page that cannot work.
  // member_unlock OR unlock_code: Code Unlock has no button to press, so
  // gating on member_unlock alone hid the tab for the one provider whose whole
  // member-facing feature is that screen.
  const canShowDoors =
    Boolean(accessProvider) && hasMemberDoorsUI(accessCapabilities);

  const hasAccessControl =
    (user?.orgPlan?.features?.some((f) => f.slug === 'access-control') ?? false) &&
    canShowDoors;

  const hasActivePlan = user?.userPlan && user.userPlan.length > 0;

  const handleRestrictedAction = (actionCallback) => {
    if (hasActivePlan) {
      actionCallback();
    } else {
      setNoPlanDialogOpen(true);
    }
  };

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/doors')) setValue('doors');
    else if (path.includes('/feed')) setValue('feed');
    else if (path.includes('/profile')) setValue('profile');
    else if (path.includes('/settings')) setValue('settings');
    else if (path.includes('/mealos')) setValue('cafe');
    else if (path.includes('/dashboard')) setValue('home');
  }, [location]);

  const handleChange = (event, newValue) => {
    if (newValue === 'doors') {
      // Guard again here, independently of the render check. The tab should
      // already be hidden without a usable provider, but a stale render, a
      // cached bundle, or a provider slug we do not recognise must never send
      // a member to a doors page that cannot load. Going nowhere beats that.
      if (!canShowDoors) {
        console.warn('Doors tapped with no usable access provider — ignoring');
        return;
      }

      // Spintly unlocks over BLE inside the native SDK, so only it needs the
      // native screen — hand that case to Flutter. Both Spintly generations
      // (ACAAS 'spintly' and SAAMS 'spintly_saams') use the same on-device SDK;
      // the Flutter side matches with AccessProvider.usesSpintlySdk. Every
      // other provider unlocks over the API, which is a plain React page, so
      // navigate here directly. That keeps the doors screen working outside
      // the app too (desktop browser, mobile web) instead of depending on the
      // bridge.
      if (accessProvider === 'spintly' || accessProvider === 'spintly_saams') {
        try {
          window.Flutter.postMessage(
            JSON.stringify({ type: 'navigate', to: 'doors' })
          );
          return;
        } catch (e) {
          console.warn('Flutter bridge not available:', e);
        }
      }
      setValue('doors');
      navigate('/user/doors');
      return;
    }

    if (newValue === 'feed') {
      if (!hasCommunityFeature) return;
      handleRestrictedAction(() => {
        setValue('feed');
        navigate('/user/feed');
      });
      return;
    }

    setValue(newValue);

    const path = {
      home: '/user/dashboard',
      cafe: '/user/mealos/cafeteria-list',
      profile: '/user/profile',
      settings: '/user/settings',
    };
    navigate(path[newValue]);
  };

  if (!isMobile) return null;

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: 'block', sm: 'none' },
          zIndex: 1000,
          transform: `translateY(${scrollDirection === 'down' ? '100%' : '0'})`,
          transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut,
          }),
          // The row already ends with 8px of its own bottom padding, so adding the
          // full inset on top of it double-counted the reserve and left a ~42px
          // empty band under the labels. Subtracting that 8px makes the gap below
          // the labels exactly env(safe-area-inset-bottom) and no more, which is
          // what every platform actually asks for: the iOS home indicator, the
          // Android gesture pill, and a 48dp Android 3-button nav bar all stay
          // fully clear. Do not trim further than the row's own padding: on
          // 3-button navigation the inset is a solid system bar, not a thin
          // overlay, so anything taken off it puts the tabs underneath it.
          paddingBottom: 'max(0px, calc(env(safe-area-inset-bottom) - 8px))',
        }}
        elevation={3}
      >
        <BottomNavigation
          value={value}
          onChange={handleChange}
          sx={{
            height: header.MOBILE_HEIGHT,
            bgcolor: 'background.paper',
            '& .MuiBottomNavigationAction-root': {
              maxWidth: 'none',
              padding: hasAccessControl ? '6px 6px 8px' : '6px 12px 8px',
              minWidth: 'auto',
              flex: 1,
            },
            '& .MuiBottomNavigationAction-label': {
              opacity: 1,
              fontSize: '0.7rem',
              '&.Mui-selected': {
                fontSize: '0.7rem',
              },
            },
          }}
        >
          <BottomNavigationAction label="Home" value="home" icon={<Home />} />
          {hasCommunityFeature && (
            <BottomNavigationAction label="Community" value="feed" icon={<Article />} />
          )}
          <BottomNavigationAction label="Profile" value="profile" icon={<Person />} />

          {hasCafeFeature ? (
            <BottomNavigationAction
              label="Cafe"
              value="cafe"
              icon={<Restaurant />}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                '@keyframes shine': {
                  '0%': { backgroundPosition: '-200% center' },
                  '100%': { backgroundPosition: '200% center' },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-200%',
                  width: '200%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  animation: 'shine 3s infinite',
                },
                '& .MuiSvgIcon-root': { position: 'relative', zIndex: 1 },
                '& .MuiBottomNavigationAction-label': { position: 'relative', zIndex: 1 },
              }}
            />
          ) : (
            <BottomNavigationAction label="Settings" value="settings" icon={<Settings />} />
          )}

          {hasAccessControl && (
            <BottomNavigationAction
              label="Doors"
              value="doors"
              icon={<MeetingRoom />}
              sx={{
                '& .MuiSvgIcon-root': { color: value === 'doors' ? 'primary.main' : 'inherit' },
              }}
            />
          )}
        </BottomNavigation>
      </Paper>

      {/* No Plan Dialog */}
      <Dialog
        open={noPlanDialogOpen}
        onClose={() => setNoPlanDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1, pt: 3 }}>
          <Icon icon="mdi:lock-alert-outline" width={48} color={theme.palette.warning.main} style={{ marginBottom: 8 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Access Restricted</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You need an active subscription plan to access the community feed.
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button variant="outlined" color="inherit" onClick={() => setNoPlanDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setNoPlanDialogOpen(false);
                navigate('/user/plans');
              }}
            >
              Explore Plans
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MobileBottomNav;